import { AxiosError } from "axios";
import {
  extractBackendErrorCode,
  getBackendErrorMessage,
} from "../constants/backend-error-messages";
import { ERROR_MESSAGES } from "../constants/error-messages";
import { IErrorResponse } from "../interfaces/all";

const NETWORK_ERROR: IErrorResponse = {
  status: "error",
  type: "internal",
  code: "NET-0001",
  message: "Erro de conexão com o servidor",
  userMessage:
    "Não foi possível conectar ao servidor. Verifique sua conexão com a internet.",
};

const UNKNOWN_ERROR: IErrorResponse = {
  status: "error",
  type: "internal",
  code: "REQ-0001",
  message: "Erro desconhecido",
  userMessage: ERROR_MESSAGES.internal,
};

export class ErrorHandler {
  static handleApiError(error: unknown): IErrorResponse {
    if (!(error instanceof AxiosError)) {
      return {
        ...UNKNOWN_ERROR,
        message: error instanceof Error ? error.message : UNKNOWN_ERROR.message,
      };
    }

    if (!error.response) {
      return NETWORK_ERROR;
    }

    const { data: responseData } = error.response;

    if (
      responseData &&
      typeof responseData === "object" &&
      "status" in responseData &&
      responseData.status === "error"
    ) {
      const errorResponse = responseData as IErrorResponse;

      // Verificar PRIMEIRO se é erro relacionado à Meta API através da mensagem ou detalhes
      // ANTES de processar o código, para garantir que erros de token Meta sejam detectados
      const errorDetails = errorResponse.details || {};
      const errorMessage =
        errorResponse.message || errorResponse.userMessage || "";
      const currentCode = errorResponse.code;
      const requestUrl = error.response?.config?.url || "";

      // Verificar se é erro de token Meta através de múltiplos indicadores
      // IMPORTANTE: Verificar também quando o código é AUTH-1004 mas o erro pode ser de token Meta
      const isMetaTokenError =
        currentCode === "BUS-3021" || // Já tem o código correto
        errorMessage.includes("Token de acesso da Meta") ||
        (errorMessage.includes("Meta") &&
          (errorMessage.includes("expirado") ||
            errorMessage.includes("inválido"))) ||
        (errorMessage.includes("token") &&
          (errorMessage.includes("expirado") ||
            errorMessage.includes("inválido"))) ||
        errorDetails?.type === "META_TOKEN_EXPIRED" ||
        errorDetails?.error?.type === "META_TOKEN_EXPIRED" ||
        (errorDetails?.error &&
          typeof errorDetails.error === "object" &&
          "type" in errorDetails.error &&
          errorDetails.error.type === "META_TOKEN_EXPIRED") ||
        // Verificar se é erro 401 em rotas relacionadas a templates/Meta API
        (error.response?.status === 401 && requestUrl.includes("/templates"));

      // Se for erro de token Meta, usar código BUS-3021 diretamente
      if (isMetaTokenError) {
        const backendCode = "BUS-3021";
        const backendMessage = getBackendErrorMessage(backendCode);
        if (backendMessage) {
          errorResponse.userMessage = backendMessage;
          errorResponse.code = backendCode;
          // Garantir que o tipo seja "business" (ApiErrorType), não "authorization"/"authentication"
          if (
            errorResponse.type === "authorization" ||
            errorResponse.type === "authentication"
          ) {
            errorResponse.type = "business";
          }
          return errorResponse;
        }
      }

      // Priorizar código do backend se já estiver presente no errorResponse
      let backendCode: string | null | undefined = currentCode;

      // Se não houver código, tentar extrair da mensagem
      if (!backendCode) {
        backendCode = extractBackendErrorCode(errorResponse.message);
      }

      // Se encontrou código do backend, usar a mensagem específica
      if (backendCode) {
        const backendMessage = getBackendErrorMessage(backendCode);
        if (backendMessage) {
          errorResponse.userMessage = backendMessage;
          errorResponse.code = backendCode;
        }
      } else if (!errorResponse.userMessage && errorResponse.message) {
        errorResponse.userMessage = errorResponse.message;
      }

      // Se ainda não temos userMessage, usar a message do backend
      if (!errorResponse.userMessage && errorResponse.message) {
        errorResponse.userMessage = errorResponse.message;
      }

      return errorResponse;
    }

    return {
      ...UNKNOWN_ERROR,
      message: error.message,
      details: responseData,
    };
  }
}
